import React from 'react';
import { type ModalProps } from '@/stores/use-modal-store.ts';
import {  useRevalidator } from 'react-router-dom';
import type { Server } from '@/models';
import { API_BASE } from '@/config';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Modal, ModalBody, ModalContent, ModalHeader } from '@heroui/react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useSocket } from '@/context/socket-context/use-external-socket';
import { localInputToUTC } from '@/utils/date-formatter.ts';

const formSchema = z.object({
  name: z
    .string()
    .min(1, {
      message: 'Event name is required.',
    })
    .max(50, {
      message: 'Event name must be less than 50 characters.',
    }),
  description: z
    .string()
    .min(1, {
      message: 'Description is required.',
    })
    .max(200, {
      message: 'Description must be less than 200 characters.',
    }),
  channelId: z.string(),
  serverId: z.string(),
  startDate: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid start date.',
  }),
  endDate: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid end date.',
  }),
  recurrenceRule: z.enum(['none', 'daily', 'weekly', 'monthly']).optional(),
  recurrenceEndDate: z
    .string()
    .optional()
    .refine((val) => !val || !isNaN(Date.parse(val)), {
      message: 'Invalid recurrence end date.',
    }),
});

/**
 * @experimental
 * @param param0
 * @param param0.isOpen
 * @param param0.onClose
 * @param param0.data
 *
 * @constructor
 *
 * @example
 * openModal('createServerEvent', {
 *   server,
 *   audioChannels,
 *   videoChannels,
 * });
 */
export const CreateServerEventModal: React.FC<ModalProps> = ({ isOpen, onClose, data }) => {
  const { server, audioChannels = [], videoChannels = [] } = data;

  const socket = useSocket();
  const { revalidate } = useRevalidator();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      channelId: '',
      serverId: server?.id,
      startDate: '',
      endDate: '',
      recurrenceRule: 'none',
      recurrenceEndDate: '',
    },
  });

  const isLoading = form.formState.isSubmitting;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const payload = {
        ...values,
        startDate: localInputToUTC(values.startDate),
        endDate: localInputToUTC(values.endDate),
        recurrenceRule: values.recurrenceRule === 'none' ? null : values.recurrenceRule,
        recurrenceEndDate: values.recurrenceEndDate
          ? localInputToUTC(values.recurrenceEndDate)
          : null,
      };

      const res = await fetch(`${API_BASE}/api/servers/${server.id}/events`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const created = await res.json();

      socket?.send(
        JSON.stringify({
          type: 'event-add',
          payload: created,
        })
      );

      toast.success('Event created');
      revalidate();
      onClose();
      form.reset();
    } catch {
      toast.error('Failed to create event');
    }
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onClose}>
      <ModalContent>
        <ModalHeader>Create event</ModalHeader>

        <ModalBody>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={isLoading} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Input {...field} disabled={isLoading} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="channelId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Channel</FormLabel>
                    <select {...field} className="w-full border rounded p-2">
                      <option value="">Select channel</option>

                      {audioChannels.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} (Audio)
                        </option>
                      ))}

                      {videoChannels.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} (Video)
                        </option>
                      ))}
                    </select>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start</FormLabel>
                    <Input type="datetime-local" {...field} />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>End</FormLabel>
                    <Input type="datetime-local" {...field} />
                  </FormItem>
                )}
              />

              <Button type="submit" disabled={isLoading}>
                Create
              </Button>
            </form>
          </Form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
